import {
  bounds,
  boundsIntersect,
  closestPointOnRay,
  dist,
  fromPolar,
  sub,
  angleOf,
  vec,
  type Anchor,
  type Vec2,
} from '../geom';
import type { ResolvedEntity } from '../scene';
import { SNAP_DEFINITIONS } from './kinds';
import type { Construction, SnapResult, SnapSettings } from './types';

export interface SnapQuery {
  readonly pointer: Vec2;
  /** Screen pixels per world unit. */
  readonly scale: number;
  readonly entities: readonly ResolvedEntity[];
  readonly reference: Anchor | null;
  readonly settings: SnapSettings;
  readonly excludeIds?: ReadonlySet<string>;
  /**
   * Points the snap engine must not offer, in world units. A drag passes the position it
   * started from: a coincident feature there would otherwise hold the handle in place.
   */
  readonly ignorePoints?: readonly Vec2[];
  readonly constructions?: readonly Construction[];
}

export interface SnapOutcome {
  readonly point: Vec2;
  readonly snap: SnapResult | null;
  readonly ortho: boolean;
}

/** Locks a point to the nearest ray of `angleStep` degrees from `reference`. */
export function applyOrtho(pointer: Vec2, reference: Vec2, angleStep: number): Vec2 {
  const delta = sub(pointer, reference);
  const distance = Math.hypot(delta.x, delta.y);
  if (distance === 0 || angleStep <= 0) return pointer;
  const angle = angleOf(delta);
  const locked = Math.round(angle / angleStep) * angleStep;
  return fromPolar(reference, distance, locked);
}

export function resolvePoint(query: SnapQuery): SnapOutcome {
  const { settings, reference } = query;
  // Touching a curve, the anchor is only its center, which nothing is drawn from.
  const ortho = settings.ortho && reference !== null && reference.mode !== 'touching';
  const pointer =
    ortho && reference
      ? applyOrtho(query.pointer, reference.point, settings.angleStep)
      : query.pointer;

  const snap = findSnap(
    { ...query, pointer },
    ortho && reference ? { reference: reference.point, pointer } : null,
  );
  if (!snap) return { point: pointer, snap: null, ortho };
  return { point: snap.point, snap, ortho };
}

interface OrthoConstraint {
  readonly reference: Vec2;
  readonly pointer: Vec2;
}

export function findSnap(
  query: SnapQuery,
  constraint: OrthoConstraint | null = null,
): SnapResult | null {
  const { settings, pointer, scale } = query;
  const definition = SNAP_DEFINITIONS.find(({ kind }) => kind === settings.kind);
  if (!definition) return null;
  const toleranceWorld = settings.tolerancePx / scale;

  const searchBounds = {
    min: vec(pointer.x - toleranceWorld * 8, pointer.y - toleranceWorld * 8),
    max: vec(pointer.x + toleranceWorld * 8, pointer.y + toleranceWorld * 8),
  };

  const constructions: ResolvedEntity[] = (query.constructions ?? []).map(
    ({ sourceId, shape }) => ({
      id: sourceId,
      type: shape.type,
      visible: true,
      selectable: false,
      layerId: '',
      shape,
    }),
  );
  const nearby = [...query.entities, ...constructions].filter(
    (entity) =>
      entity.visible &&
      !query.excludeIds?.has(entity.id) &&
      boundsIntersect(bounds(entity.shape), searchBounds),
  );

  const context = {
    pointer,
    reference: query.reference,
    gridSize: settings.gridSize,
    nearby,
  };

  let best: SnapResult | null = null;
  for (const candidate of definition.provide(context)) {
    if (isIgnored(candidate.point, query)) continue;
    if (constraint && dist(onRay(constraint, candidate.point), candidate.point) > toleranceWorld)
      continue;
    const distancePx = dist(candidate.point, pointer) * scale;
    if (distancePx > settings.tolerancePx) continue;
    if (!best || distancePx < best.distancePx)
      best = { kind: definition.kind, ...candidate, distancePx };
  }

  if (!best) return null;
  return constraint ? { ...best, point: onRay(constraint, best.point) } : best;
}

/** Within half a pixel of a point the caller asked to skip. */
function isIgnored(point: Vec2, query: SnapQuery): boolean {
  return (query.ignorePoints ?? []).some((ignored) => dist(ignored, point) * query.scale <= 0.5);
}

/** The closest point to `point` on the ray from the reference through the locked pointer. */
function onRay(constraint: OrthoConstraint, point: Vec2): Vec2 {
  return closestPointOnRay(
    constraint.reference,
    sub(constraint.pointer, constraint.reference),
    point,
  );
}
